/**
 * AudioFileUpload Component
 *
 * Drag-and-drop multi-file audio uploader for voice cloning.
 * Validates file types, sizes, and displays file list with removal option.
 */

import { useCallback, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, Music, AlertCircle, FileAudio } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IAudioFileUploadProps } from '@/@types/voices';
import { VOICE_CONSTANTS } from '@/@types/voices';

/**
 * Format file size to human-readable string
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AudioFileUpload({
  files,
  onFilesChange,
  maxFiles = VOICE_CONSTANTS.MAX_AUDIO_FILES,
  maxFileSizeMB = VOICE_CONSTANTS.MAX_AUDIO_FILE_SIZE_MB,
  acceptedTypes = [...VOICE_CONSTANTS.ACCEPTED_AUDIO_TYPES],
  disabled = false,
}: IAudioFileUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      const isValidType =
        acceptedTypes.some(type => file.type === type) ||
        file.type.startsWith('audio/') ||
        VOICE_CONSTANTS.ACCEPTED_AUDIO_EXTENSIONS.some(ext =>
          file.name.toLowerCase().endsWith(ext)
        );

      if (!isValidType) {
        return `"${file.name}" is not a supported audio format. Use MP3, WAV, M4A, WebM, or OGG.`;
      }

      // Check file size
      if (file.size > maxFileSizeBytes) {
        return `"${file.name}" exceeds the ${maxFileSizeMB}MB size limit.`;
      }

      return null;
    },
    [acceptedTypes, maxFileSizeBytes, maxFileSizeMB]
  );

  const handleAddFiles = useCallback(
    (newFiles: FileList | File[]) => {
      setError(null);
      const fileArray = Array.from(newFiles);

      // Check total file count
      if (files.length + fileArray.length > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed. You have ${files.length} files.`);
        return;
      }

      // Validate each file
      const validFiles: File[] = [];
      for (const file of fileArray) {
        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          return;
        }

        // Check for duplicates by name and size
        const isDuplicate = files.some(
          existing => existing.name === file.name && existing.size === file.size
        );
        if (!isDuplicate) {
          validFiles.push(file);
        }
      }

      if (validFiles.length > 0) {
        onFilesChange([...files, ...validFiles]);
      }
    },
    [files, maxFiles, onFilesChange, validateFile]
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      onFilesChange(newFiles);
      setError(null);
    },
    [files, onFilesChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        handleAddFiles(droppedFiles);
      }
    },
    [disabled, handleAddFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (selectedFiles && selectedFiles.length > 0) {
        handleAddFiles(selectedFiles);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleAddFiles]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const canAddMore = files.length < maxFiles;
  const acceptString = VOICE_CONSTANTS.ACCEPTED_AUDIO_EXTENSIONS.join(',');

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        role="button"
        tabIndex={!disabled && canAddMore ? 0 : -1}
        aria-disabled={disabled || !canAddMore}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
          isDragOver && !disabled
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
            : 'border-gray-300 dark:border-gray-700',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled &&
            canAddMore &&
            'hover:border-gray-400 dark:hover:border-gray-600 cursor-pointer'
        )}
        onClick={!disabled && canAddMore ? handleBrowseClick : undefined}
        onKeyDown={
          !disabled && canAddMore
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleBrowseClick();
                }
              }
            : undefined
        }
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptString}
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled || !canAddMore}
        />

        <div className="flex flex-col items-center gap-2">
          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full">
            <Upload className="h-6 w-6 text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {canAddMore ? 'Drop audio files here or click to browse' : 'Maximum files reached'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              MP3, WAV, M4A, WebM, OGG up to {maxFileSizeMB}MB each
            </p>
          </div>
          {canAddMore && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={e => {
                e.stopPropagation();
                handleBrowseClick();
              }}
              disabled={disabled}
            >
              Browse Files
            </Button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* File Count */}
      <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
        <span>
          {files.length} of {maxFiles} files selected
        </span>
        {files.length >= VOICE_CONSTANTS.MIN_AUDIO_FILES && (
          <span className="text-green-600 dark:text-green-400">Ready to clone</span>
        )}
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${file.size}`}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
            >
              <div className="p-2 bg-gray-200 dark:bg-gray-700 rounded">
                <FileAudio className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveFile(index)}
                disabled={disabled}
                className="text-gray-400 hover:text-red-500"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Recommendations */}
      {files.length === 0 && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-lg">
          <Music className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium">For best results:</p>
            <ul className="list-disc list-inside text-xs mt-1 space-y-0.5 text-blue-600 dark:text-blue-400">
              <li>Upload 1-3 minutes of clear audio</li>
              <li>Use recordings with minimal background noise</li>
              <li>Speak naturally with varied intonation</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export { AudioFileUpload };
